import { createSlice } from '@reduxjs/toolkit';
import { resetSubscriptionState } from './subscriptionSlice';

const initialState = {
  token: null,
  refreshToken: null,
  user: null,
  isTokenValidating: false,
  showSessionExpiredModal: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (state, action) => {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken || null;
      state.user = action.payload.user;
      state.showSessionExpiredModal = false;
    },
    logout: (state) => {
      state.token = null;
      state.refreshToken = null;
      state.user = null;
      // Ne pas fermer la modal ici : elle est ouverte avant le logout
      // et doit rester visible jusqu'à ce que l'utilisateur clique sur "Se reconnecter"
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
    updateCredits: (state, action) => {
      if (state.user) {
        state.user.credits = action.payload;
      }
    },
    setTokenValidating: (state, action) => {
      state.isTokenValidating = action.payload;
    },
    showSessionExpiredModal: (state) => {
      state.showSessionExpiredModal = true;
    },
    hideSessionExpiredModal: (state) => {
      state.showSessionExpiredModal = false;
    },
  },
});

export const { 
  loginSuccess, 
  logout, 
  updateUser, 
  updateCredits, 
  setTokenValidating,
  showSessionExpiredModal,
  hideSessionExpiredModal
} = authSlice.actions;

export const performLogout = () => (dispatch) => {
  dispatch(logout());
  dispatch(resetSubscriptionState());
  // Nettoyer le localStorage
  localStorage.removeItem('persist:root');
};

// Refresh silencieux : échange le refresh token contre un nouvel access token
let isRefreshInProgress = false;
let refreshPromise = null;

export const refreshAccessToken = () => async (dispatch, getState) => {
  const { refreshToken } = getState().auth;
  if (!refreshToken) return false;

  // Éviter les appels parallèles — partager la même promesse
  if (isRefreshInProgress) return refreshPromise;

  isRefreshInProgress = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/users/refresh-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        dispatch(showSessionExpiredModal());
        dispatch(performLogout());
        return false;
      }
      const data = await response.json();
      dispatch(loginSuccess({ token: data.token, refreshToken: data.refreshToken, user: getState().auth.user }));
      return true;
    } catch {
      return false;
    } finally {
      isRefreshInProgress = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Variable pour éviter les validations multiples simultanées
let isValidationInProgress = false;
let lastValidationTime = 0;
const VALIDATION_DEBOUNCE = 5000; // 5 secondes

// Action asynchrone pour vérifier la validité du token
export const validateToken = () => async (dispatch, getState) => {
  const { token, isTokenValidating } = getState().auth;
  
  if (!token) {
    return false;
  }

              // Vérifier si la validation est désactivée (pendant l'upload par exemple)
              if (window.disableTokenValidation) {
                return true;
              }

              // Éviter les validations multiples simultanées
              if (isValidationInProgress || isTokenValidating) {
                return true;
              }

              // Débounce : éviter les validations trop fréquentes
              const now = Date.now();
              if (now - lastValidationTime < VALIDATION_DEBOUNCE) {
                return true;
              }

  isValidationInProgress = true;
  lastValidationTime = now;
  dispatch(setTokenValidating(true));
  
              try {
                const response = await fetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/users/profile', {
                  method: 'GET',
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                });

    if (!response.ok) {
      if (response.status === 401) {
        const refreshed = await dispatch(refreshAccessToken());
        if (refreshed) {
          const newToken = getState().auth.token;
          const retry = await fetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/users/profile', {
            method: 'GET',
            headers: { Authorization: `Bearer ${newToken}` },
          });
          if (retry.ok) {
            const userData = await retry.json();
            dispatch(updateUser(userData));
            return true;
          }
        }
        if (!getState().auth.token) {
          return false;
        }
        if (!getState().auth.refreshToken) {
          dispatch(showSessionExpiredModal());
          dispatch(performLogout());
        }
        return false;
      }
      throw new Error('Erreur lors de la vérification du token');
    }

    const userData = await response.json();
    dispatch(updateUser(userData));
                return true;
              } catch (error) {
                // Erreur réseau (timeout, serveur indisponible...) : on ne déconnecte pas
                // pour ne pas expulser l'utilisateur sur une panne temporaire
                console.warn('validateToken: erreur réseau, validation ignorée', error.message);
                return true;
              } finally {
                isValidationInProgress = false;
                dispatch(setTokenValidating(false));
              }
};

export default authSlice.reducer;
